import { transformUrnToWitboostId } from '@agilelab/plugin-wb-builder-common';
import { customAlertApiRef } from '@agilelab/plugin-wb-platform';
import {
  RoleAssignments,
  RoleSubjectEntity,
  rbacRoleEditPermission,
} from '@agilelab/plugin-wb-rbac-common';
import { Entity } from '@backstage/catalog-model';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import useAsyncFn, { AsyncState } from 'react-use/lib/useAsyncFn';
import { rbacApiRef } from '../../..';

export interface Filters {
  text?: string;
}
export interface AssignmentsContextProviderProps {
  children?: ReactNode;
  roleId: string;
}
export interface AssignmentsContextType
  extends AssignmentsContextProviderProps {
  openManageDrawer: boolean;
  setOpenManageDrawer: Dispatch<SetStateAction<boolean>>;

  openEditEnabledScopesDrawer: boolean;
  setEditEnabledScopesDrawer: Dispatch<SetStateAction<boolean>>;

  canEditRolePermission: boolean;

  openConfirmDialog: boolean;
  setOpenConfirmDialog: Dispatch<SetStateAction<boolean>>;

  removeAssignments: (assignmentSubjects: string[]) => Promise<string[]>;

  updateAssignments: (
    userOrGroup: string,
    scopes: {
      urn: string;
      enabled: boolean;
    }[],
    upsert?: boolean,
  ) => Promise<RoleSubjectEntity[]>;

  loadAssignments: () => Promise<RoleAssignments[]>;
  assignmentsLoaded: AsyncState<RoleAssignments[] | undefined>;

  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;

  selectedRemoveItems: RoleAssignments[];
  setSelectedRemoveItems: Dispatch<SetStateAction<RoleAssignments[]>>;

  selectedRow: RoleAssignments | undefined;
  setSelectedRow: Dispatch<SetStateAction<RoleAssignments | undefined>>;
}

export const AssignmentsContext = createContext<AssignmentsContextType>(
  {} as AssignmentsContextType,
);

export const AssignmentsContextProvider = ({
  children,
  roleId,
}: AssignmentsContextProviderProps) => {
  const rbacApi = useApi(rbacApiRef);
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(customAlertApiRef);

  // check if permission check is enabled
  const permissionsEnabled =
    configApi.getOptionalBoolean('permission.enabled') ?? false;

  const editPermissionAllowed = usePermission({
    permission: rbacRoleEditPermission,
  }).allowed;

  const canEditRolePermission = !permissionsEnabled || editPermissionAllowed;

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const [filters, setFilters] = useState<Filters>({});

  const [openManageDrawer, setOpenManageDrawer] = useState(false);

  const [openEditEnabledScopesDrawer, setEditEnabledScopesDrawer] =
    useState(false);

  const [selectedRemoveItems, setSelectedRemoveItems] = useState<
    RoleAssignments[]
  >([]);

  const [selectedRow, setSelectedRow] = useState<RoleAssignments>();

  const [assignmentsLoaded, loadAssignments] = useAsyncFn(async () => {
    const fetchAssignments = async (): Promise<RoleAssignments[]> => {
      const res: RoleSubjectEntity[] = await rbacApi.getRolesSubjects({
        searchKeyword: filters.text,
        filters: { roleId },
        options: {
          token: (await identityApi.getCredentials()).token,
        },
      });

      const entityMap = new Map<string, Entity>();

      // Fetch entity objects for entityRefs and subjects
      await Promise.all(
        res.map(async item => {
          if (
            item.entityRef &&
            !entityMap.has(item.entityRef) &&
            item.entityRef !== '*'
          ) {
            try {
              const urn = item.entityRef;

              const entity = await catalogApi.getEntityByRef(
                transformUrnToWitboostId(urn)!,
              );

              if (entity) {
                entityMap.set(item.entityRef, entity);
              }
            } catch (e) {
              alertApi.post({
                message: `Error while resolving entity with ref: ${item.entityRef}`,
                severity: 'error',
              });
            }
          }
          if (item.subject && !entityMap.has(item.subject)) {
            try {
              const entity = await catalogApi.getEntityByRef(item.subject);
              if (entity) {
                entityMap.set(item.subject, entity);
              }
            } catch (e) {
              alertApi.post({
                message: `Error while resolving entity with ref: ${item.subject}`,
                severity: 'error',
              });
            }
          }
        }),
      );

      // Transform RoleSubjectEntity[] into RoleAssignments[] grouped by subject
      const grouped = res.reduce((acc, item) => {
        if (!item.subject) return acc;
        const entity = entityMap.get(item.subject);

        if (!acc[item.subject]) {
          acc[item.subject] = {
            roleId: item.roleId!,
            scopes: [],
            userOrGroup: { urn: item.subject, entity: entity! },
          };
        }

        if (item.entityRef) {
          acc[item.subject].scopes.push(
            item.entityRef === '*'
              ? { urn: '*' }
              : {
                  urn: item.entityRef,
                  entity: entityMap.get(item.entityRef),
                  enabled: item.enabled,
                },
          );
        }

        return acc;
      }, {} as Record<string, RoleAssignments>);

      return Object.values(grouped);
    };

    return fetchAssignments();
  }, [filters]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const removeAssignments = async (assignmentSubjects: string[]) => {
    try {
      const token = (await identityApi.getCredentials()).token;

      const availableEditors = await rbacApi.getAvailableEditors({
        options: { token },
      });

      if (availableEditors === assignmentSubjects.length) {
        throw new Error(
          `Cannot remove all subjects from role '${roleId}' with 'rbac.role.edit' — operation would leave no users with this permission`,
        );
      }

      const results = await Promise.allSettled(
        assignmentSubjects.map(subject =>
          rbacApi.deleteRolesSubjects({
            filters: { roleId, subject },
            options: { token },
          }),
        ),
      );

      const failed = results.filter(result => result.status === 'rejected');

      if (failed.length > 0) {
        failed.forEach(failure => {
          const customError = (failure as any).reason.body.error;
          alertApi.post({
            message: `${customError.message}: ${customError.errorMessage}`,
            severity: 'error',
          });
        });
      } else {
        alertApi.post({
          message: `Assignments successfully removed`,
          severity: 'success',
        });
      }

      setSelectedRemoveItems([]);
      loadAssignments();
      return [];
    } catch (e) {
      setOpenManageDrawer(false);
      alertApi.post({
        message: e.message || 'An error occurred',
        severity: 'error',
      });
      return [];
    } finally {
      setSelectedRow(undefined);
    }
  };

  const updateAssignments = async (
    userOrGroup: string,
    scopes: { urn: string; enabled: boolean }[],
  ) => {
    try {
      const token = (await identityApi.getCredentials()).token;

      const previousScopeMap = new Map(
        (selectedRow?.scopes || []).map(scope => [scope.urn, scope.enabled]),
      );

      const changedScopes = scopes.filter(scope => {
        return previousScopeMap.get(scope.urn) !== scope.enabled;
      });

      const rolesSubjects = changedScopes.map(scope => ({
        subject: userOrGroup,
        roleId: roleId,
        entityRef: scope.urn,
        enabled: scope.enabled ?? true,
      }));

      const results = await rbacApi.addRolesSubjects({
        rolesSubjects,
        options: { token, upsert: true },
      });
      setEditEnabledScopesDrawer(false);
      loadAssignments();

      alertApi.post({
        message: `Successfully updates ${results.length} assignment(s).`,
        severity: results.length ? 'success' : 'warning',
      });

      return results;
    } catch (e) {
      setEditEnabledScopesDrawer(false);
      alertApi.post({
        message: e.message || 'An error occurred while adding assignments.',
        severity: 'error',
      });
      return [];
    } finally {
      setSelectedRow(undefined);
    }
  };

  return (
    <AssignmentsContext.Provider
      value={{
        roleId,

        loadAssignments,
        assignmentsLoaded,

        canEditRolePermission,

        openConfirmDialog,
        setOpenConfirmDialog,

        openManageDrawer,
        setOpenManageDrawer,

        openEditEnabledScopesDrawer,
        setEditEnabledScopesDrawer,

        removeAssignments,

        filters,
        setFilters,

        selectedRemoveItems,
        setSelectedRemoveItems,

        selectedRow,
        setSelectedRow,

        updateAssignments,
      }}
    >
      {children}
    </AssignmentsContext.Provider>
  );
};

export const useAssignmentsContext = () => {
  const context = useContext(AssignmentsContext);
  if (!context) {
    throw new Error(
      'useAssignmentsContext must be used within a AssignmentsContextProvider',
    );
  }
  return context as AssignmentsContextType;
};
